import React, { useState } from 'react';
import Module from '../../../../layout/Module';
import FileUploaderPreviewList from './FileUploaderPreviewList';
import FileDropZone from '../../../../commom/FileDropZone';
import isEmpty from 'lodash/isEmpty';
import globalConfig from '../../../../../config/globalConfig';
import { uploadFile, presigneUrl, deleteFiles } from '../../../../../services/upload/upload.service';
import { assessmentMarkingBulk } from '../../../../../services/data-entry/assessments.service';
import { handlerErrorV2 } from '../../../../../utils/errors';
import { toggleAlertModule } from '../../../../../store/actions/alert.actions';
import { useDispatch, useSelector } from 'react-redux';
import { updateAssessmentMarkingAnswer } from '../../../../../store/actions/dataEntryAssessmentMarkingActions';

const bucketFolder = 'dataentry/assessments/files';
const maxFileSize = 100; //100MB for now
const fileSizeUnit = 'MB';

//Read about supported MIME types https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types
const acceptedFileTypes = {
  'image/*': [],
  'audio/*': [],
  'video/*': [],
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.ms-powerpoint': ['.ppt'],
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.oasis.opendocument.text': ['.odt'],
  'application/vnd.amazon.ebook': ['.azw'],
  'text/plain': ['.txt']
};

const FileUploader = ({ question, record, assessmentId, handleClose }) => {
  const { answer = [], questionId } = question;
  const { _id: recordId } = record;
  const [files, setFiles] = useState(answer ?? []);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentUpload, setCurrentUpload] = useState(null);
  const [percent, setPercent] = useState(0);
  const [uploaded, setUploaded] = useState([]);

  const markingList = useSelector((state) => state.dataEntryAssessmentMarking?.marking?.list ?? []);
  const tenant = useSelector((state) => state?.tenant?.tenant ?? {});
  const dispatch = useDispatch();
  const pendingToUpload = files?.filter((el) => !el?.alreadyAdded || el?.error);

  const getChangedRecord = () => {
    const foundRecord = markingList?.find((record) => record.hasChanged && record?._id === recordId);

    if (!foundRecord) {
      return null;
    }

    const { _id, date, comments, questions = [] } = foundRecord;
    const changedRecord = {
      _id,
      date,
      comments,
      questions: questions
        ?.filter((question) => question.hasChanged)
        ?.map((question) => {
          return {
            questionId: question.questionId,
            answer: question.answer || []
          };
        })
    };

    return changedRecord;
  };

  const updateRecord = async () => {
    const recordToSave = getChangedRecord();
    await assessmentMarkingBulk({
      assessmentId,
      records: [recordToSave]
    });
  };

  const uploadFiles = async () => {
    if (isEmpty(files)) {
      setError('Please select at least one file.');
      return;
    }
    setIsLoading(true);
    setError(null);
    setPercent(0);
    setCurrentUpload(null);
    setUploaded([]);
    try {
      const filesToUpload = files.filter((file) => !file.alreadyAdded || file.error);

      const resultUpload = await presigneUrl(filesToUpload, bucketFolder);
      if (resultUpload.error || !resultUpload.data) {
        return { error: resultUpload.error.message };
      } else {
        const result = [];
        for (let indexFile in resultUpload.data) {
          const file = resultUpload.data[indexFile];

          const { data, error } = file;
          if (!data) {
            result.push({
              id: error.file,
              name: error.file,
              link: `${bucketFolder}/${error.file}`,
              error: error?.msg || 'Something went wrong. Please try again.'
            });
          }

          setCurrentUpload(data?.file?.name);
          setPercent(((Number(indexFile) + 1) / resultUpload.data.length) * 100);

          const currentPhoto = files.find((item) => item.name === data?.file?.name);

          if (currentPhoto) {
            let error = null;
            await uploadFile(data.presignedUrl, currentPhoto.file).catch((err) => {
              error = handlerErrorV2(err);
            });
            result.push({
              id: data?.filename,
              name: data?.file?.name,
              size: data?.file?.size,
              type: data?.file?.type,
              lastModified: data?.file?.lastModified,
              path: data?.file?.path,
              alreadyAdded: error ? false : true,
              link: `${globalConfig.storage || window.location.origin}/tenants/${
                tenant?.tenantId
              }/${bucketFolder}/${data.filename}`,
              error
            });
            setUploaded((prev) => [...prev, { name: data?.file?.name, error }]);
          }
        }

        if (!result) {
          return { error: 'Something went wrong. Please try again.' };
        } else {
          dispatch(toggleAlertModule());
          const uploadedSuccess = result.filter((el) => !el.error);

          dispatch(
            updateAssessmentMarkingAnswer({
              recordId: record?._id,
              code: record?.code,
              questionId,
              answer: [...answer, ...uploadedSuccess]
            })
          );
          const newState = files.map((el) => {
            const foundFile = uploadedSuccess.find((item) => item.name === el.name);
            if (foundFile) {
              return {
                ...el,
                alreadyAdded: true,
                link: foundFile.link
              };
            }
            return el;
          });

          setFiles(newState);
          updateRecord();
          return { error: null, data: result };
        }
      }
    } catch (error) {
      const message = handlerErrorV2(error);
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const onDeleteFile = async (file) => {
    const { id, name, alreadyAdded = false } = file;
    const newFiles = files.filter((el) => el?.name !== name);

    if (!alreadyAdded) {
      setFiles(newFiles);
    } else {
      const fileToDelete = files.find((el) => el?.id === id);

      if (fileToDelete) {
        const { data } = await deleteFiles([{ ...fileToDelete, name: fileToDelete.id }], bucketFolder);

        if (data) {
          const filterAnswer = answer.filter((el) => el?.id !== id);

          dispatch(
            updateAssessmentMarkingAnswer({
              recordId: record?._id,
              code: record?.code,
              questionId,
              answer: filterAnswer ?? []
            })
          );

          setFiles(newFiles);
          await updateRecord();
        }
      }
    }
  };

  const onUploadFile = async (file) => {
    try {
      if (file) {
        const loadingFiles = files.map((el) => {
          if (el?.name === file.name) {
            return { ...el, isUploading: true };
          }
          return el;
        });

        setFiles(loadingFiles);

        const resultUpload = await presigneUrl([file], bucketFolder);

        if (resultUpload.error || !resultUpload.data) {
          return { error: resultUpload.error.message };
        } else {
          const { data } = resultUpload;
          const fileData = data && data?.length ? data[0]?.data : null;

          if (fileData) {
            let error = null;
            await uploadFile(fileData.presignedUrl, file?.file).catch((err) => {
              error = handlerErrorV2(err);
            });

            const customData = {
              id: fileData?.filename,
              name: fileData?.file?.name,
              size: fileData?.file?.size,
              type: fileData?.file?.type,
              lastModified: fileData?.file?.lastModified,
              path: fileData?.file?.path,
              alreadyAdded: error ? false : true,
              isUploading: false,
              link: `${globalConfig.storage || window.location.origin}/tenants/${
                tenant?.tenantId
              }/${bucketFolder}/${fileData.filename}`,
              error
            };

            const newFiles = files.map((el) => {
              if (el?.name === file.name) {
                return {
                  ...el,
                  ...customData
                };
              }
              return el;
            });
            setFiles(newFiles);

            const justAdded = {
              ...file,
              ...customData
            };

            dispatch(
              updateAssessmentMarkingAnswer({
                recordId: record?._id,
                code: record?.code,
                questionId,
                answer: [...answer, justAdded]
              })
            );

            updateRecord();
            return { error: null, data: justAdded };
          } else {
          }
        }
      }
    } catch (err) {
      const message = handlerErrorV2(error);
      setError(message);
    }
  };

  return (
    <>
      <Module.BodyContainer>
        <FileDropZone
          files={files}
          setFiles={setFiles}
          isLoading={isLoading || percent === 100}
          acceptedFileTypes={acceptedFileTypes}
          maxFileSize={maxFileSize}
          fileSizeUnit={fileSizeUnit}
          supportedFormatsDescription={'Documents and images'}
        />

        <FileUploaderPreviewList
          files={files}
          setFiles={setFiles}
          isLoading={isLoading}
          uploaded={uploaded}
          onDeleteFile={onDeleteFile}
          onUploadFile={onUploadFile}
          uploadFiles={uploadFiles}
          pendingToUpload={pendingToUpload}
          percent={percent}
          error={error}
          currentUpload={currentUpload}
        />
      </Module.BodyContainer>
    </>
  );
};

export default FileUploader;
